import {AlertCircle, CheckCircle2, Info, X} from 'lucide-react';
import React, {useState} from 'react';

import {cn} from '../../lib/utils';
import {DecorativeCircles} from '../DecorativeCircles';
import {Alert as AlertRoot, AlertDescription, AlertTitle} from '../shadcn/alert';
import {Button} from '../shadcn/button';

export type AlertVariant = 'error' | 'warning' | 'info' | 'success';

export const alertVariantKeys: AlertVariant[] = ['error', 'warning', 'info', 'success'];

export interface AlertActionItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
}

export interface AlertProps {
  variant: AlertVariant;
  description: string | string[];
  title?: string;
  descriptionColor?: 'variant' | 'secondary';
  closable?: boolean;
  actions?: AlertActionItem[];
  className?: string;
}

const variantConfig = {
  error: {
    border: 'border-border-error_subtle',
    background: 'bg-background-error',
    iconColor: 'text-text-error',
    hoverIconColor: 'hover:text-text-error',
    descriptionColor: 'text-indication-error',
    cssVar: 'var(--color-text-error)',
    Icon: AlertCircle
  },
  warning: {
    border: 'border-border-warning',
    background: 'bg-background-warning',
    iconColor: 'text-text-warning',
    hoverIconColor: 'hover:text-text-warning',
    descriptionColor: 'text-indication-warning',
    cssVar: 'var(--color-text-warning)',
    Icon: AlertCircle
  },
  info: {
    border: 'border-border-info',
    background: 'bg-background-info',
    iconColor: 'text-text-info',
    hoverIconColor: 'hover:text-text-info',
    descriptionColor: 'text-indication-info',
    cssVar: 'var(--color-text-info)',
    Icon: Info
  },
  success: {
    border: 'border-border-success',
    background: 'bg-background-success',
    iconColor: 'text-text-success',
    hoverIconColor: 'hover:text-text-success',
    descriptionColor: 'text-indication-success',
    cssVar: 'var(--color-text-success)',
    Icon: CheckCircle2
  }
} as const;

export function Alert({
  variant,
  description,
  title,
  descriptionColor = 'variant',
  closable,
  actions,
  className
}: AlertProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const {
    border,
    background,
    iconColor,
    hoverIconColor,
    descriptionColor: variantDescriptionColor,
    cssVar,
    Icon
  } = variantConfig[variant];
  const resolvedDescriptionColor =
    descriptionColor === 'secondary' ? 'text-fg-secondary' : variantDescriptionColor;
  const messages = Array.isArray(description) ? description : [description];

  return (
    <AlertRoot
      className={cn(
        'relative flex items-start gap-4 rounded-xl p-4 shadow-xs',
        border,
        background,
        closable && 'pe-12',
        className
      )}
    >
      <DecorativeCircles
        size={56}
        color={cssVar}
        strokeWidth={2}
        rings={[
          {ratio: 0.25, opacity: 0.3},
          {ratio: 0.34, opacity: 0.1}
        ]}
        className="mt-0.5 shrink-0 size-5"
      >
        <Icon aria-hidden="true" className={cn('size-5', iconColor)} />
      </DecorativeCircles>

      <div className="flex min-w-0 flex-1 flex-col gap-3 ms-1">
        <div className="flex flex-col gap-1">
          {title != null && (
            <AlertTitle className={cn('font-semibold', iconColor)}>{title}</AlertTitle>
          )}
          {messages.length === 1 ? (
            <AlertDescription className={resolvedDescriptionColor}>{messages[0]}</AlertDescription>
          ) : (
            <AlertDescription className={resolvedDescriptionColor}>
              <ul className="list-disc space-y-0.5 ps-4">
                {messages.map((msg, index) => (
                  <li key={`${index}-${msg}`}>{msg}</li>
                ))}
              </ul>
            </AlertDescription>
          )}
        </div>
        {actions != null && actions.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {actions.map(action => (
              <Button
                key={action.label}
                type="button"
                variant="tertiary"
                size="sm"
                onClick={action.onClick}
                className={cn(
                  'h-auto gap-1 bg-transparent p-0 text-sm font-semibold [&_svg]:size-4',
                  iconColor,
                  hoverIconColor
                )}
              >
                {action.iconPosition !== 'end' && action.icon}
                {action.label}
                {action.iconPosition === 'end' && action.icon}
              </Button>
            ))}
          </div>
        )}
      </div>

      {closable && (
        <Button
          size="icon-sm"
          variant="tertiary"
          className={cn('absolute top-2 end-2 hover:bg-transparent', iconColor, hoverIconColor)}
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
        >
          <X />
        </Button>
      )}
    </AlertRoot>
  );
}
